import type { ReactNode } from 'react'
import { useId, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { XMarkIcon } from '@gto/icons-react/outline/x-mark'
import { Autocomplete } from '@gto/ui/autocomplete'
import { Button } from '@gto/ui/button'
import { Field } from '@gto/ui/field'

import type { AffiliateAdminPartnerStatusCountsSchema } from '@/api/gen'
import { FacetChip } from '@/components/facet-chip'
import { SelectField } from '@/components/form'
import { COUNTRY_OPTIONS, countryName } from '@/utils/countries'
import { countryFlagEmoji } from '@/utils/country-flag'
import type {
  AccountFacet,
  BetaFacet,
  FullyMigratedFacet,
  PartnerSegment,
  PartnersSearch,
  PartnerStatus,
  TermsAcceptedFacet,
} from '@/utils/partners-search-params'
import { activeFacetCount } from '@/utils/partners-search-params'

type TierOption = {
  id: number
  name: string
}

type PartnersFilterBarProps = {
  search: PartnersSearch
  tiers: TierOption[]
  /** Global per-status counts — rendered inside the Status options so the
   *  team sees the distribution without applying the filter. */
  statusCounts?: AffiliateAdminPartnerStatusCountsSchema
  /** Right-aligned slot in the toolbar row — the page puts search + date
   *  range here so ALL filtering input lives on one row. */
  endSlot?: ReactNode
  onChange: (next: Partial<PartnersSearch>) => void
  onClearFacets: () => void
}

const STATUS_OPTIONS: {
  value: PartnerStatus
  labelId: string
  countKey: keyof AffiliateAdminPartnerStatusCountsSchema
}[] = [
  {
    value: 'PENDING_APPROVAL',
    labelId: 'admin.partners.status.pending',
    countKey: 'pending_approval',
  },
  { value: 'ACTIVE', labelId: 'admin.partners.status.active', countKey: 'active' },
  { value: 'SUSPENDED', labelId: 'admin.partners.status.suspended', countKey: 'suspended' },
  { value: 'REJECTED', labelId: 'admin.partners.status.rejected', countKey: 'rejected' },
]

// The segment values come from the BE — kept here as a sealed list (label
// lookup goes through i18n message ids, so adding/removing options never
// reaches into the page component).
const SEGMENT_OPTIONS: { value: PartnerSegment; labelId: string }[] = [
  { value: 'never_converted', labelId: 'admin.partners.segment.neverConverted' },
  { value: 'churned', labelId: 'admin.partners.segment.churned' },
  { value: 'top_performers', labelId: 'admin.partners.segment.topPerformers' },
  { value: 'no_payout_method', labelId: 'admin.partners.segment.needsSetup' },
]

const ACCOUNT_OPTIONS: { value: AccountFacet; labelId: string }[] = [
  { value: 'linked', labelId: 'admin.partners.account.linked' },
  { value: 'unlinked', labelId: 'admin.partners.account.unlinked' },
]

const BETA_OPTIONS: { value: BetaFacet; labelId: string }[] = [
  { value: 'yes', labelId: 'admin.partners.beta.on' },
  { value: 'no', labelId: 'admin.partners.beta.off' },
]

const FULLY_MIGRATED_OPTIONS: { value: FullyMigratedFacet; labelId: string }[] = [
  { value: 'yes', labelId: 'admin.partners.fullyMigrated.on' },
  { value: 'no', labelId: 'admin.partners.fullyMigrated.off' },
]

const TERMS_ACCEPTED_OPTIONS: { value: TermsAcceptedFacet; labelId: string }[] = [
  { value: 'yes', labelId: 'admin.partners.termsAccepted.on' },
  { value: 'no', labelId: 'admin.partners.termsAccepted.off' },
]

// `<TextField select>` uses the empty string for "no selection"; the URL
// schema uses `undefined`. This sentinel boundary keeps the conversion in
// one place so the page never has to deal with the form-control quirk.
const ALL = ''

// The four two-value facets share one shape (a yes/no-style select + a removable
// chip). Only the typed read/apply differ per key, so those stay as per-facet
// closures (no cast needed) while the JSX renders from one map. `status` (counts),
// `tier` (numeric), `country` (autocomplete) and `segment` are genuinely bespoke
// and stay explicit below — folding them in would be the wrong abstraction.
type BooleanFacet = {
  key: string
  labelId: string
  anyLabelId: string
  minWidthClass: string
  options: readonly { value: string; labelId: string }[]
  read: (search: PartnersSearch) => string | undefined
  apply: (value: string) => Partial<PartnersSearch>
}

const BOOLEAN_FACETS: readonly BooleanFacet[] = [
  {
    key: 'account',
    labelId: 'admin.partners.account',
    anyLabelId: 'admin.partners.filter.account.any',
    minWidthClass: 'min-w-[140px]',
    options: ACCOUNT_OPTIONS,
    read: (search) => search.account,
    apply: (value) => ({ account: ACCOUNT_OPTIONS.find((opt) => opt.value === value)?.value }),
  },
  {
    key: 'beta',
    labelId: 'admin.partners.beta',
    anyLabelId: 'admin.partners.filter.beta.any',
    minWidthClass: 'min-w-[140px]',
    options: BETA_OPTIONS,
    read: (search) => search.beta,
    apply: (value) => ({ beta: BETA_OPTIONS.find((opt) => opt.value === value)?.value }),
  },
  {
    key: 'fullyMigrated',
    labelId: 'admin.partners.fullyMigrated',
    anyLabelId: 'admin.partners.filter.fullyMigrated.any',
    minWidthClass: 'min-w-[160px]',
    options: FULLY_MIGRATED_OPTIONS,
    read: (search) => search.fullyMigrated,
    apply: (value) => ({
      fullyMigrated: FULLY_MIGRATED_OPTIONS.find((opt) => opt.value === value)?.value,
    }),
  },
  {
    key: 'termsAccepted',
    labelId: 'admin.partners.termsAccepted',
    anyLabelId: 'admin.partners.filter.termsAccepted.any',
    minWidthClass: 'min-w-[150px]',
    options: TERMS_ACCEPTED_OPTIONS,
    read: (search) => search.termsAccepted,
    apply: (value) => ({
      termsAccepted: TERMS_ACCEPTED_OPTIONS.find((opt) => opt.value === value)?.value,
    }),
  },
]

const countryLabel = (code: string) => `${countryFlagEmoji(code)} ${countryName(code)}`

type CountryFilterProps = {
  label: string
  /** Applied country CODE from the URL search params. */
  value: string | undefined
  onApply: (country: string | undefined) => void
}

/** Searchable pick-one country facet. The input text is controlled and synced
 *  back to the APPLIED filter on external changes (chip removal) and on blur,
 *  so typed-but-uncommitted text never masquerades as an active filter. */
const CountryFilter = ({ label, value, onApply }: CountryFilterProps) => {
  const { formatMessage } = useIntl()
  const inputId = useId()
  const applied = value === undefined ? '' : countryLabel(value)
  const [text, setText] = useState(applied)
  const [prevApplied, setPrevApplied] = useState(applied)
  if (applied !== prevApplied) {
    setPrevApplied(applied)
    setText(applied)
  }

  return (
    <Field.Root className="min-w-[190px]">
      <Field.Label htmlFor={inputId}>{label}</Field.Label>
      <Autocomplete.Root
        openOnInputClick
        itemToStringValue={countryLabel}
        items={COUNTRY_OPTIONS}
        value={text}
        onValueChange={(next) => {
          setText(next)
          if (next === '') {
            onApply(undefined)

            return
          }

          // onValueChange is the canonical Base UI commit path (mouse AND
          // keyboard Enter) — reverse-map the committed label to its code.
          const code = COUNTRY_OPTIONS.find((option) => countryLabel(option) === next)
          if (code !== undefined) {
            onApply(code)
          }
        }}
      >
        <Autocomplete.Input
          id={inputId}
          endAddon={
            text === '' ? undefined : (
              <Autocomplete.Clear aria-label={formatMessage({ id: 'common.clear' })}>
                <XMarkIcon className="size-4" />
              </Autocomplete.Clear>
            )
          }
          onBlur={() => {
            setText(applied)
          }}
        />
        <Autocomplete.Portal>
          <Autocomplete.Positioner align="start">
            <Autocomplete.Popup className="min-w-[190px]">
              <Autocomplete.List>
                {(code: string) => (
                  <Autocomplete.Item
                    key={code}
                    value={code}
                  >
                    {countryLabel(code)}
                  </Autocomplete.Item>
                )}
              </Autocomplete.List>
            </Autocomplete.Popup>
          </Autocomplete.Positioner>
        </Autocomplete.Portal>
      </Autocomplete.Root>
    </Field.Root>
  )
}

/**
 * Toolbar row of facet selects + the active-chip row underneath. Single source
 * of truth for facet state lives in the route's search params; this component
 * is presentation only — every user interaction calls `onChange` with the
 * delta and the page commits it to the URL.
 *
 * Marketing-team rule: the dropdowns are the INPUTS (always visible — fast
 * scan of what's available), the chips are the STATE (also visible — fast
 * scan of what's currently applied, and one-click removal). Both at once is
 * deliberate redundancy: the page never hides its current filter state behind
 * a "show filters" toggle.
 */
export const PartnersFilterBar = ({
  search,
  tiers,
  statusCounts,
  endSlot,
  onChange,
  onClearFacets,
}: PartnersFilterBarProps) => {
  const { formatMessage, formatNumber } = useIntl()
  const facetCount = activeFacetCount(search)

  const activeTier = tiers.find((t) => t.id === search.tier)
  const withCount = (label: string, count: number | undefined) =>
    count === undefined ? label : `${label} (${formatNumber(count)})`

  return (
    <div className="flex flex-col gap-4">
      {/* Single toolbar row: every filter input lives here — facet selects on
          the left, search + date range (endSlot) on the right. */}
      <div className="flex flex-wrap items-center gap-3">
        <SelectField
          className="min-w-[150px]"
          label={formatMessage({ id: 'admin.partners.filter.status' })}
          value={search.status ?? ALL}
          options={[
            {
              value: ALL,
              label: withCount(
                formatMessage({ id: 'admin.partners.status.all' }),
                statusCounts?.total,
              ),
            },
            ...STATUS_OPTIONS.map((opt) => ({
              value: opt.value,
              label: withCount(formatMessage({ id: opt.labelId }), statusCounts?.[opt.countKey]),
            })),
          ]}
          onValueChange={(v) => {
            onChange({ status: STATUS_OPTIONS.find((opt) => opt.value === v)?.value })
          }}
        />

        <SelectField
          className="min-w-[140px]"
          label={formatMessage({ id: 'admin.partners.tier' })}
          value={search.tier?.toString() ?? ALL}
          options={[
            { value: ALL, label: <FormattedMessage id="admin.partners.filter.tier.all" /> },
            ...tiers.map((tier) => ({ value: tier.id.toString(), label: tier.name })),
          ]}
          onValueChange={(v) => {
            onChange({ tier: v === ALL ? undefined : Number(v) })
          }}
        />

        {BOOLEAN_FACETS.map((facet) => (
          <SelectField
            key={facet.key}
            className={facet.minWidthClass}
            label={formatMessage({ id: facet.labelId })}
            value={facet.read(search) ?? ALL}
            options={[
              { value: ALL, label: <FormattedMessage id={facet.anyLabelId} /> },
              ...facet.options.map((opt) => ({
                value: opt.value,
                label: <FormattedMessage id={opt.labelId} />,
              })),
            ]}
            onValueChange={(v) => {
              onChange(facet.apply(v))
            }}
          />
        ))}

        <CountryFilter
          label={formatMessage({ id: 'admin.partners.country' })}
          value={search.country}
          onApply={(country) => {
            onChange({ country })
          }}
        />

        <SelectField
          className="min-w-[170px]"
          label={formatMessage({ id: 'admin.partners.filter.segment' })}
          value={search.segment ?? ALL}
          options={[
            { value: ALL, label: <FormattedMessage id="admin.partners.segment.all" /> },
            ...SEGMENT_OPTIONS.map((opt) => ({
              value: opt.value,
              label: <FormattedMessage id={opt.labelId} />,
            })),
          ]}
          onValueChange={(v) => {
            onChange({ segment: SEGMENT_OPTIONS.find((opt) => opt.value === v)?.value })
          }}
        />

        {endSlot !== undefined && (
          <div className="ml-auto flex flex-wrap items-center gap-3">{endSlot}</div>
        )}
      </div>

      {/* Active-chip row: only renders when at least one facet is active. Two
          chips minimum to render "Clear all" — at one active facet the chip
          itself is one click to clear, so a separate "Clear all" is redundant. */}
      {facetCount > 0 && (
        <div
          className="flex flex-wrap items-center gap-3"
          data-tst="partners-active-facets"
        >
          {search.status !== undefined && (
            <FacetChip
              labelId="admin.partners.filter.status"
              value={formatMessage({
                id:
                  STATUS_OPTIONS.find((s) => s.value === search.status)?.labelId ??
                  'admin.partners.status.all',
              })}
              onRemove={() => onChange({ status: undefined })}
            />
          )}
          {activeTier !== undefined && (
            <FacetChip
              labelId="admin.partners.tier"
              value={activeTier.name}
              onRemove={() => onChange({ tier: undefined })}
            />
          )}
          {BOOLEAN_FACETS.map((facet) => {
            const current = facet.read(search)
            if (current === undefined) {
              return null
            }

            return (
              <FacetChip
                key={facet.key}
                labelId={facet.labelId}
                value={formatMessage({
                  id:
                    facet.options.find((opt) => opt.value === current)?.labelId ?? facet.anyLabelId,
                })}
                onRemove={() => onChange(facet.apply(ALL))}
              />
            )
          })}
          {search.country !== undefined && (
            <FacetChip
              labelId="admin.partners.country"
              value={`${countryFlagEmoji(search.country)} ${countryName(search.country)}`}
              onRemove={() => onChange({ country: undefined })}
            />
          )}
          {search.segment !== undefined && (
            <FacetChip
              labelId="admin.partners.filter.segment"
              value={formatMessage({
                id:
                  SEGMENT_OPTIONS.find((s) => s.value === search.segment)?.labelId ??
                  'admin.partners.segment.all',
              })}
              onRemove={() => onChange({ segment: undefined })}
            />
          )}
          {facetCount > 1 && (
            <Button
              data-tst="partners-clear-facets"
              size="small"
              variant="text"
              onClick={onClearFacets}
            >
              <FormattedMessage id="admin.partners.filter.clearAll" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
